import { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [articleId]);

  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-28 flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-file-unknow-line text-2xl text-foreground-300"></i>
          </div>
          <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-2">文章未找到</h2>
          <p className="text-sm text-foreground-500 mb-6">该文章不存在或已被移除</p>
          <button
            onClick={() => navigate('/')}
            className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            返回首页
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedData = article.relatedArticles
    .map((id) => articles.find((a) => a.id === id))
    .filter(Boolean) as typeof articles;

  const articleSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.summary,
      datePublished: article.date,
      author: {
        '@type': 'Organization',
        name: '佐山爱官方资料站',
      },
      publisher: {
        '@type': 'Organization',
        name: '佐山爱官方资料站',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://yyloviest.cn/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '分类浏览',
          item: 'https://yyloviest.cn/category',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.categoryName,
          item: `https://yyloviest.cn/category?tab=${article.category}`,
        },
        {
          '@type': 'ListItem',
          position: 4,
          name: article.title,
        },
      ],
    },
  ];

  if (article.faqItems.length > 0) {
    articleSchema.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: article.faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
  }

  return (
    <>
      <head>
        <title>{article.title} | 佐山爱官方资料站</title>
        <meta name="description" content={article.summary} />
        <meta name="keywords" content={article.tags.join(',')} />
        <link rel="canonical" href={`https://yyloviest.cn/article/${article.id}`} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.summary} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://yyloviest.cn/article/${article.id}`} />
        <meta property="article:published_time" content={article.date} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.summary} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />
      </head>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 md:pt-28">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '分类浏览', path: '/category' },
                { label: article.categoryName, path: `/category?tab=${article.category}` },
                { label: article.title },
              ]}
            />

            {/* Article Header */}
            <article>
              <header className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Link
                    to={`/category?tab=${article.category}`}
                    className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {article.categoryName}
                  </Link>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i> {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i> 阅读时间约 {article.readTime} 分钟
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> 来源：佐山爱官方资料站
                  </span>
                </div>
              </header>

              {/* Featured Image */}
              <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden mb-10">
                <img
                  src={article.heroImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                  title={`${article.title} - 佐山爱官方资料站`}
                />
              </div>

              {/* Article Content */}
              <div
                className="prose-custom mb-12"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-10 pt-6 border-t border-background-200/70">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* FAQ within article */}
              {article.faqItems.length > 0 && (
                <section className="mb-12">
                  <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-4">
                    相关问答
                  </h2>
                  <div className="space-y-3">
                    {article.faqItems.map((faq, index) => (
                      <details
                        key={index}
                        className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden"
                      >
                        <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100/70 transition-colors">
                          <span className="font-heading text-sm font-medium text-foreground-950 pr-4">
                            {faq.question}
                          </span>
                          <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-foreground-400 group-open:hidden">
                            <i className="ri-add-line text-sm"></i>
                          </span>
                          <span className="w-5 h-5 flex-shrink-0 hidden group-open:flex items-center justify-center text-foreground-400">
                            <i className="ri-subtract-line text-sm"></i>
                          </span>
                        </summary>
                        <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                          {faq.answer}
                        </div>
                      </details>
                    ))}
                  </div>
                </section>
              )}

              {/* Related Articles */}
              {relatedData.length > 0 && (
                <section className="mb-16">
                  <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-5">
                    相关文章
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {relatedData.map((rel) => (
                      <Link
                        key={rel.id}
                        to={`/article/${rel.id}`}
                        className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
                      >
                        <div className="h-36 overflow-hidden">
                          <img
                            src={rel.cardImage}
                            alt={rel.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4">
                          <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 mb-2 whitespace-nowrap">
                            {rel.categoryName}
                          </span>
                          <h3 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-150 line-clamp-2">
                            {rel.title}
                          </h3>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </article>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}